import type { Dispatch, MutableRefObject, SetStateAction } from 'react';
import { useCallback, useEffect, useState } from 'react';

export interface UseAdvisorWrittenFeedbackInlineStateOptions {
  messageId: string;
  writtenFeedbackMessageId: string | null;
  setWrittenFeedbackMessageId: Dispatch<SetStateAction<string | null>>;
  kinFeedbackSubmitPendingRef: MutableRefObject<boolean>;
  closeKinInlineFeedbackRef: MutableRefObject<(() => void) | null>;
  confirmThumbDown: (comment: string | null) => Promise<boolean>;
}

export interface UseAdvisorWrittenFeedbackInlineStateResult {
  writtenFeedbackOpen: boolean;
  /** Pass to {@link useAgentAssistantMessageFeedback} as ``onThumbDownIntent`` when using ``thumbDownMode: 'intent'``. */
  onThumbDownIntent: () => void;
  /**
 * Values for {@link AgentAssistantWrittenFeedbackInline} — empty when this row’s card is not open (avoids a sync ``useEffect``).
 */
  inlineDraft: string;
  setInlineDraft: (value: string) => void;
  inlinePending: 'skip' | 'submit' | null;
  onInlineRequestClose: () => void;
  onInlineSkip: () => void;
  onInlineSubmit: () => void;
  /** True while skip/submit is awaiting ``confirmThumbDown`` (use for thumb row disabled state). */
  submitOrSkipInFlight: boolean;
}

/**
 * Shared drawer-level state for inline thumbs-down written feedback (Kin + partner assistant rows).
 * Keeps ``kinFeedbackSubmitPendingRef`` / ``closeKinInlineFeedbackRef`` in sync with submit and Escape flows.
 */
export function useAdvisorWrittenFeedbackInlineState(
  options: UseAdvisorWrittenFeedbackInlineStateOptions
): UseAdvisorWrittenFeedbackInlineStateResult {
  const {
    messageId,
    writtenFeedbackMessageId,
    setWrittenFeedbackMessageId,
    kinFeedbackSubmitPendingRef,
    closeKinInlineFeedbackRef,
    confirmThumbDown,
  } = options;

  const writtenFeedbackOpen = writtenFeedbackMessageId === messageId;
  const [downFeedbackDraft, setDownFeedbackDraft] = useState('');
  const [downFeedbackPending, setDownFeedbackPending] = useState<'skip' | 'submit' | null>(null);

  const onThumbDownIntent = useCallback(() => {
    setDownFeedbackDraft('');
    setWrittenFeedbackMessageId(messageId);
  }, [messageId, setWrittenFeedbackMessageId]);

  const inlineDraft = writtenFeedbackOpen ? downFeedbackDraft : '';
  const inlinePending = writtenFeedbackOpen ? downFeedbackPending : null;

  const downFeedbackPendingActive = !!downFeedbackPending;
  useEffect(() => {
    if (!downFeedbackPendingActive) {
      return;
    }
    kinFeedbackSubmitPendingRef.current = true;
    return () => {
      kinFeedbackSubmitPendingRef.current = false;
    };
  }, [downFeedbackPendingActive, kinFeedbackSubmitPendingRef]);

  useEffect(() => {
    if (!writtenFeedbackOpen) {
      return;
    }
    const closeInline = () => {
      setWrittenFeedbackMessageId(null);
      setDownFeedbackDraft('');
    };
    closeKinInlineFeedbackRef.current = closeInline;
    return () => {
      if (closeKinInlineFeedbackRef.current === closeInline) {
        closeKinInlineFeedbackRef.current = null;
      }
    };
  }, [writtenFeedbackOpen, closeKinInlineFeedbackRef, setWrittenFeedbackMessageId]);

  const handleWrittenFeedbackConfirm = useCallback(
    async (comment: string | null, mode: 'skip' | 'submit') => {
      setDownFeedbackPending(mode);
      let succeeded = false;
      try {
        succeeded = await confirmThumbDown(comment);
      } finally {
        setDownFeedbackPending(null);
      }
      if (succeeded) {
        setDownFeedbackDraft('');
        setWrittenFeedbackMessageId(null);
      }
    },
    [confirmThumbDown, setWrittenFeedbackMessageId]
  );

  const onInlineSkip = useCallback(() => {
    void handleWrittenFeedbackConfirm(null, 'skip');
  }, [handleWrittenFeedbackConfirm]);

  const onInlineSubmit = useCallback(() => {
    void handleWrittenFeedbackConfirm(downFeedbackDraft.trim() || null, 'submit');
  }, [downFeedbackDraft, handleWrittenFeedbackConfirm]);

  const onInlineRequestClose = useCallback(() => {
    if (downFeedbackPending) {
      return;
    }
    setWrittenFeedbackMessageId(null);
    setDownFeedbackDraft('');
  }, [downFeedbackPending, setWrittenFeedbackMessageId]);

  return {
    writtenFeedbackOpen,
    onThumbDownIntent,
    inlineDraft,
    setInlineDraft: setDownFeedbackDraft,
    inlinePending,
    onInlineRequestClose,
    onInlineSkip,
    onInlineSubmit,
    submitOrSkipInFlight: downFeedbackPendingActive,
  };
}
